import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { sortersMapping } from '../../../shared/src/services/games-sorting.service';
import { sortingTypesEnum } from '../../../shared/src/enums/sorting-types.enum';

export const useApplySubcategoriesActionsToGames = (gameIds = []) => {
    const {
        subcategoriesActions: {
            sortBy,
            filterBy,
        },
    } = useSelector(state => state.app);
    const { gamesById } = useSelector(state => state.casino);

    const filteredBy = useMemo(() => {
        const filteredByProviders = filterBy.providers?.length > 0;
        return {
            some: filteredByProviders,
            providers: filteredByProviders,
        };
    }, [filterBy]);

    const initialIds = (sortBy || filteredBy.some) ? [] : [...gameIds];
    const [filteredGameIds, setFilteredGameIds] = useState(initialIds);
    const [processedGameIds, setProcessedGameIds] = useState(initialIds);

    const applyFiltering = useCallback(gameIds => {
        if (!filteredBy.some) {
            return gameIds;
        }
        const enabledProviders = filteredBy.providers ? filterBy.providers : [];
        const filterByProviders = provider => enabledProviders.includes(provider);

        return [...gameIds].filter(gameId => {
            const game = gamesById?.[gameId];
            let matchFilter = false;

            if (filteredBy.providers) {
                const gameProviders = [
                    game?.externalGameSubProvider,
                    game?.externalGameProvider,
                ].filter(Boolean);
                const matchedProviders = gameProviders.filter(filterByProviders);
                if (matchedProviders.length > 0) {
                    matchFilter = true;
                }
            }

            return matchFilter;
        });
    }, [filterBy, gamesById]);

    const applySorting = useCallback(gameIds => {
        if (!sortBy) {
            return gameIds;
        }

        const sorter = sortersMapping[sortBy] || sortersMapping[sortingTypesEnum.ANY];

        return [...gameIds].sort((aId, bId) => {
            const gameA = gamesById?.[aId];
            const gameB = gamesById?.[bId];

            if (!gameA && !gameB) {
                return 0;
            }

            return (gameA && !gameB && -1)
                || (!gameA && gameB && 1)
                || sorter(gameA, gameB);
        });
    }, [sortBy, gamesById]);

    // Filter games
    useEffect(() => {
        const gameIdsAfterFiltering = applyFiltering(gameIds);
        const isEqual =
            gameIdsAfterFiltering.length === filteredGameIds.length &&
            gameIdsAfterFiltering.every((id, i) => id === filteredGameIds[i]);

        if (!isEqual) {
            setFilteredGameIds(gameIdsAfterFiltering);
        }
    }, [applyFiltering, gameIds]);

    // Sort and set processed games
    useEffect(() => {
        const gameIdsAfterSorting = applySorting(filteredGameIds);
        const isEqual =
            gameIdsAfterSorting.length === processedGameIds.length &&
            gameIdsAfterSorting.every((id, i) => id === processedGameIds[i]);

        if (!isEqual) {
            setProcessedGameIds(gameIdsAfterSorting);
        }
    }, [applySorting, filteredGameIds]);

    return processedGameIds;
};
